import { cn } from 'cn';
import { useDeferredValue } from 'react';
import type { FieldError, UseFormReturn } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import Icon from '../../Icon/Icon';

interface InputMaxLengthProps {
  name: string;
  maxLength: number;
}

interface InputFeedbackProps {
  error?: FieldError | false;
  errorId: string;
  errorMessage?: (error: FieldError) => string;
  hideError?: boolean;
  maxLength?: number;
  name: string;
}

function InputMaxLength({
  name,
  maxLength,
}: InputMaxLengthProps): React.JSX.Element {
  const fieldValue = useDeferredValue(useWatch({ name }));
  const currentLength = fieldValue?.length ?? 0;

  return (
    <div className="mt-2 flex justify-end">
      <span
        className={cn('typ-ui-sm', {
          'text-neutral-100': currentLength <= maxLength,
          'text-red-100': currentLength > maxLength,
        })}
        data-testid={`max-length-${name}`}
      >
        {`${currentLength}/${maxLength}`}
      </span>
    </div>
  );
}

export function InputFeedback({
  error,
  errorId,
  errorMessage,
  hideError,
  maxLength,
  name,
}: InputFeedbackProps): React.JSX.Element {
  const form = useFormContext();
  const hasMaxLength =
    maxLength && maxLength > 0 && (form as UseFormReturn | null);

  return (
    <>
      {hasMaxLength ? (
        <InputMaxLength name={name} maxLength={maxLength} />
      ) : null}
      {error ? (
        <div
          id={errorId}
          data-testid={`error-${name}`}
          className={cn(
            'typ-headline-2xs mt-2 flex items-start justify-start space-x-1 text-red-100',
            {
              hidden: hideError,
            },
          )}
        >
          <Icon type="alert-diamond" solid size="tiny" />
          <span className="flex-1">
            {typeof errorMessage === 'function'
              ? errorMessage(error)
              : error.message}
          </span>
        </div>
      ) : null}
    </>
  );
}
